import {
  mergeTyped,
  useDataStream,
  useStreamValue,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { mapTo } from 'rxjs/operators';
import { updateProject } from '../../../api/fetch-functions/project';
import { ProjectAPIRequestBodyType } from '../../../api/types/project/api-request-body';
import { maxStringLength } from '../../../constants/max-string-length';
import { viewTexts } from '../../../constants/view-texts';
import { useFetchResultState } from '../../../functions/hooks/use-fetch-result-state';
import {
  FormInputStateConfigType,
  useFormInputState
} from '../../../functions/hooks/use-form-input-state';
import { useToken } from '../../../global-state/auth-state';
import { fetchProjectListOf } from '../../../global-state/database/project-list';
import { IProjectType } from '../../../types/record/project';
import { EditProjectDialogView } from './edit-project-dialog-view';

const projectNameInputStateConfig: FormInputStateConfigType<ProjectAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.projectName,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.projectName,
  required: true,
  validationErrorContentMapFn: content =>
    content.name !== undefined ? content.name.toArray() : []
};

interface Props {
  open: boolean;
  onClose: (reason: 'ok' | 'cancel' | '') => void;
  companyId: number;
  project: IProjectType;
}

export const EditProjectDialog: FC<Props> = memo(
  ({ open, onClose, companyId, project: projectInit }: Readonly<Props>) => {
    const token = useToken();

    /* state */

    const [
      // dummy comment
      fetchResult,
      setFetchResult
    ] = useFetchResultState<IProjectType, ProjectAPIRequestBodyType>();

    const nonFieldErrors = useMemo<string[]>(
      () =>
        fetchResult !== undefined &&
        fetchResult.type === 'error' &&
        fetchResult.body.type === 'non-field-errors'
          ? fetchResult.body.content.toArray()
          : [],
      [fetchResult]
    );

    const [
      projectName$,
      projectNameErrors$,
      projectNameIsValid$,
      projectNameOnInput
    ] = useFormInputState<IProjectType, ProjectAPIRequestBodyType>(
      open,
      fetchResult,
      projectNameInputStateConfig,
      projectInit.name
    );

    const [submitOnClick$, submitOnClick] = useVoidEventAsStream();

    const submitButtonEnabled$ = useDataStream<boolean>(
      false,
      mergeTyped(submitOnClick$.pipe(mapTo(false)), projectNameIsValid$)
    );

    /* extract values */

    const submitButtonEnabled = useStreamValue(submitButtonEnabled$, false);
    const projectName = useStreamValue(projectName$);
    const projectNameErrors = useStreamValue(projectNameErrors$);

    /* event handlers */

    const closeOnClick = useCallback(
      (reason: '' | 'cancel' | 'ok') => {
        if (reason !== 'ok') {
          onClose(reason);
        } else {
          submitOnClick();
          if (token !== undefined && projectName !== undefined) {
            updateProject(token)(projectInit.id)({
              name: projectName,
              companyId
            }).then(res => {
              setFetchResult(res);
              if (res.type === 'success') {
                onClose(reason);
                fetchProjectListOf(companyId);
              }
            });
          }
        }
      },
      [
        onClose,
        companyId,
        projectInit,
        projectName,
        token,
        setFetchResult,
        submitOnClick
      ]
    );

    return (
      <EditProjectDialogView
        open={open}
        onClose={closeOnClick}
        projectName={projectName}
        projectNameErrors={projectNameErrors}
        projectNameOnInput={projectNameOnInput}
        submitButtonEnabled={submitButtonEnabled}
        nonFieldErrors={nonFieldErrors}
      />
    );
  }
);

EditProjectDialog.displayName = 'EditProjectDialog';
